import React from 'react';
import styled from 'styled-components';
import { Shield, Plus, Edit3, Lock, Unlock, MoreHorizontal, MapPin, Globe, FileText, Phone, Briefcase, Key, Moon, Sun } from 'react-feather';
import PageTemplate from '../common/templates/PageTemplate';
import { useThemeContext } from '../../../styles/ThemeContext';

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────
const WALLET_USERS = [
  { name: 'James Whitfield', email: 'j.whitfield@apexfin.com', initials: 'JW', role: 'Admin', active: true, bg: '#000000' },
  { name: 'Sarah Chen', email: 's.chen@apexfin.com', initials: 'SC', role: 'Admin', active: true, bg: '#000000' },
  { name: 'Marco Delgado', email: 'm.delgado@apexfin.com', initials: 'MD', role: 'Viewer', active: true, bg: '#000000' },
  { name: 'Priya Kapoor', email: 'p.kapoor@apexfin.com', initials: 'PK', role: 'Viewer', active: false, bg: '#000000' },
];

const MOCK_USER = {
  name: 'Apex Financial LLC',
  email: 'contact@apexfin.com',
  initials: 'AF',
  role: 'Primary Account',
  bg: '#000000',
  details: {
    address: '120 Broadway, Suite 300',
    country: 'United States',
    taxId: 'XX-XXXX123',
    phone: '+1 (555) 019-8234',
    industry: 'Financial Services'
  }
};

// ─────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────
const ContentPanel = styled.div`
  background: ${p => p.theme.colors.background.paper};
  border-radius: 12px;
  padding: 20px;
  box-shadow: ${p => p.theme.shadows.sm};
  margin-bottom: 20px;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const CurrentUserAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: ${p => p.$bg === '#000000' ? p.theme.colors.primary.main : p.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.contrast};
  flex-shrink: 0;
`;

const CurrentUserDetails = styled.div`
  flex: 1;
`;

const CurrentUserName = styled.h2`
  margin: 0 0 5px;
  font-size: 20px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.primary};
`;

const CurrentUserEmail = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.primary};
  margin: 0 0 16px;
`;

const ProfileDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${p => p.theme.colors.border.main};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DetailIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${p => p.theme.colors.background.selected};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.colors.primary.main};
  flex-shrink: 0;
`;

const DetailTextWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.disabled};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.primary};
`;

/* Wallet Permissions */
const InfoBanner = styled.div`display:flex;align-items:center;gap:9px;padding:10px 14px;background:${p => p.theme.colors.background.selected};border:1px solid ${p => p.theme.colors.border.main};border-radius:10px;margin-bottom:14px;`;
const InfoBannerText = styled.p`font-size:12px;color:${p => p.theme.colors.text.primary};margin:0;font-weight:500;`;
const WpHeader = styled.div`display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:14px;gap:12px;`;
const WpTitleWrap = styled.div`display:flex;align-items:center;gap:10px;`;
const WpTitle = styled.p`font-size:13.5px;font-weight:700;color:${p => p.theme.colors.text.primary};margin:0;`;
const WpSub = styled.p`font-size:11.5px;color:${p => p.theme.colors.text.disabled};margin:3px 0 0;`;
const InviteBtn = styled.button`display:flex;align-items:center;gap:5px;padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;background:${p => p.theme.colors.primary.main};color:${p => p.theme.colors.text.contrast};border:none;white-space:nowrap;transition:opacity 0.18s;&:hover{opacity:0.85;}`;

const MobileEditBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.text.primary};
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
  
  @media (max-width: 1024px) {
    display: flex;
  }
`;
const RolesTable = styled.div`border-radius:12px;border:1px solid ${p => p.theme.colors.border.main};overflow:hidden;overflow-x: auto;`;
const RolesHead = styled.div`display:grid;grid-template-columns:1fr 120px 80px 80px;gap:8px;padding:9px 14px;background:${p => p.theme.colors.background.selected};border-bottom:1px solid ${p => p.theme.colors.border.main};min-width: 500px;`;
const RolesHeadCell = styled.span`font-size:10.5px;font-weight:700;color:${p => p.theme.colors.text.disabled};text-transform:uppercase;letter-spacing:0.5px;`;
const RolesRow = styled.div`display:grid;grid-template-columns:1fr 120px 80px 80px;gap:8px;padding:12px 14px;border-bottom:1px solid ${p => p.theme.colors.background.selected};align-items:center;transition:background 0.15s;min-width: 500px;&:last-child{border-bottom:none;}&:hover{background:${p => p.theme.colors.background.selected};}`;
const UserCellWrap = styled.div`display:flex;align-items:center;gap:10px;`;
const UserAvatar = styled.div`width:34px;height:34px;border-radius:9px;background:${p => p.$bg === '#000000' ? p.theme.colors.primary.main : p.$bg};display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:${p => p.theme.colors.text.contrast};flex-shrink:0;`;
const UserName = styled.p`font-size:13px;font-weight:600;color:${p => p.theme.colors.text.primary};margin:0;`;
const UserEmail = styled.p`font-size:11px;color:${p => p.theme.colors.text.disabled};margin:2px 0 0;`;
const RolePill = styled.span`font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap;width: max-content;background:${p => p.$role === 'Admin' ? p.theme.colors.primary.main : p.theme.colors.background.selected};color:${p => p.$role === 'Admin' ? p.theme.colors.text.contrast : p.theme.colors.text.primary};`;

const StatusMark = styled.span`display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:${p => p.$active ? p.theme.colors.text.primary : p.theme.colors.text.disabled};&::before{content:'';width:7px;height:7px;border-radius:50%;background:${p => p.$active ? p.theme.colors.secondary.main : p.theme.colors.border.main};}`;
const RowActions = styled.div`display:flex;gap:3px;`;
const RowIconBtn = styled.button`width:26px;height:26px;background:none;border:none;border-radius:6px;cursor:pointer;display:flex;align-items:center;justify-content:center;color:${p => p.theme.colors.text.disabled};transition:background 0.15s,color 0.15s;&:hover{background:${p => p.theme.colors.background.selected};color:${p => p.theme.colors.text.primary};}`;

const SecurityItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid ${p => p.theme.colors.border.main};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SecurityText = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecurityLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.primary};
  margin: 0 0 4px;
`;

const SecuritySub = styled.p`
  font-size: 12px;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.theme.colors.background.selected};
  color: ${p => p.theme.colors.text.primary};
  border: 1px solid ${p => p.theme.colors.border.main};
  transition: all 0.2s;

  &:hover {
    background: ${p => p.theme.colors.primary.main};
    color: ${p => p.theme.colors.text.contrast};
    border-color: ${p => p.theme.colors.primary.main};
  }
`;

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
const ProfileSettingsScreen = ({ onLogout }) => {
  const { isDarkMode, toggleTheme } = useThemeContext();

  return (
    <PageTemplate title="Profile & Settings" onLogout={onLogout}>
      <ContentPanel>
        <SectionTitle>My Profile</SectionTitle>
        <ProfileHeader>
          <CurrentUserAvatar $bg={MOCK_USER.bg}>{MOCK_USER.initials}</CurrentUserAvatar>
          <CurrentUserDetails>
            <CurrentUserName>{MOCK_USER.name}</CurrentUserName>
            <CurrentUserEmail>{MOCK_USER.email}</CurrentUserEmail>
            <RolePill $role={MOCK_USER.role}>{MOCK_USER.role}</RolePill>
          </CurrentUserDetails>
        </ProfileHeader>

        <ProfileDetailsGrid>
          <DetailItem>
            <DetailIconWrap><MapPin size={16} /></DetailIconWrap>
            <DetailTextWrap>
              <DetailLabel>Address</DetailLabel>
              <DetailValue>{MOCK_USER.details.address}</DetailValue>
            </DetailTextWrap>
          </DetailItem>
          <DetailItem>
            <DetailIconWrap><Globe size={16} /></DetailIconWrap>
            <DetailTextWrap>
              <DetailLabel>Country</DetailLabel>
              <DetailValue>{MOCK_USER.details.country}</DetailValue>
            </DetailTextWrap>
          </DetailItem>
          <DetailItem>
            <DetailIconWrap><FileText size={16} /></DetailIconWrap>
            <DetailTextWrap>
              <DetailLabel>Tax ID / EIN</DetailLabel>
              <DetailValue>{MOCK_USER.details.taxId}</DetailValue>
            </DetailTextWrap>
          </DetailItem>
          <DetailItem>
            <DetailIconWrap><Phone size={16} /></DetailIconWrap>
            <DetailTextWrap>
              <DetailLabel>Phone Number</DetailLabel>
              <DetailValue>{MOCK_USER.details.phone}</DetailValue>
            </DetailTextWrap>
          </DetailItem>
          <DetailItem>
            <DetailIconWrap><Briefcase size={16} /></DetailIconWrap>
            <DetailTextWrap>
              <DetailLabel>Industry</DetailLabel>
              <DetailValue>{MOCK_USER.details.industry}</DetailValue>
            </DetailTextWrap>
          </DetailItem>
        </ProfileDetailsGrid>
      </ContentPanel>

      <ContentPanel>
        <SectionTitle>User Roles & Permissions</SectionTitle>
        <InfoBanner>
          <Shield size={14} color="#374151" />
          <InfoBannerText>Manage who can view, send, and approve transactions on this profile. Changes take effect immediately.</InfoBannerText>
        </InfoBanner>
        <WpHeader>
          <div>
            <WpTitleWrap>
              <WpTitle>Team Members</WpTitle>
              <MobileEditBtn title="Edit Members"><Edit3 size={14} /></MobileEditBtn>
            </WpTitleWrap>
            <WpSub>4 users · 2 Admin, 2 Viewer</WpSub>
          </div>
          <InviteBtn><Plus size={13} /> Invite User</InviteBtn>
        </WpHeader>
        <RolesTable>
          <RolesHead>
            <RolesHeadCell>User</RolesHeadCell><RolesHeadCell>Role</RolesHeadCell>
            <RolesHeadCell>Status</RolesHeadCell><RolesHeadCell>Actions</RolesHeadCell>
          </RolesHead>
          {WALLET_USERS.map((u, i) => (
            <RolesRow key={i}>
              <UserCellWrap>
                <UserAvatar $bg={u.bg}>{u.initials}</UserAvatar>
                <div><UserName>{u.name}</UserName><UserEmail>{u.email}</UserEmail></div>
              </UserCellWrap>
              <RolePill $role={u.role}>{u.role}</RolePill>
              <StatusMark $active={u.active}>{u.active ? 'Active' : 'Inactive'}</StatusMark>
              <RowActions>
                <RowIconBtn title="Edit"><Edit3 size={11} /></RowIconBtn>
                <RowIconBtn title={u.active ? 'Deactivate' : 'Activate'}>{u.active ? <Lock size={11} /> : <Unlock size={11} />}</RowIconBtn>
                <RowIconBtn title="More"><MoreHorizontal size={11} /></RowIconBtn>
              </RowActions>
            </RolesRow>
          ))}
        </RolesTable>
      </ContentPanel>

      <ContentPanel>
        <SectionTitle>Security & Authentication</SectionTitle>
        <SecurityItem>
          <SecurityText>
            <SecurityLabel>Change Password</SecurityLabel>
            <SecuritySub>Update your password to keep your account secure.</SecuritySub>
          </SecurityText>
          <ActionBtn><Key size={14} /> Update</ActionBtn>
        </SecurityItem>
      </ContentPanel>

      <ContentPanel>
        <SectionTitle>Preferences</SectionTitle>
        <SecurityItem>
          <SecurityText>
            <SecurityLabel>Dark Mode</SecurityLabel>
            <SecuritySub>Toggle between light and dark themes.</SecuritySub>
          </SecurityText>
          <ActionBtn onClick={toggleTheme} style={{ minWidth: '100px', justifyContent: 'center' }}>
            {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
            {isDarkMode ? 'Light' : 'Dark'}
          </ActionBtn>
        </SecurityItem>
      </ContentPanel>
    </PageTemplate>
  );
};

export default ProfileSettingsScreen;
